import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { linkStudent, searchStudents } from '../../services/teacherService';
import { useDebounce } from '../../hooks/useDebounce';
import { Student } from '../../types/teacher';

interface LinkStudentDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function LinkStudentDialog({ open, onClose, onSuccess }: LinkStudentDialogProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Ref for the search input to manage focus
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Debounce search term to avoid excessive API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchTerm('');
      setSearchResults([]);
      setSelectedStudent(null);
      setError(null);
      setSearchError(null);
    }
  }, [open]);

  // Focus the search input when dialog opens
  useEffect(() => {
    if (open) {
      // Increase timeout to ensure dialog transition is complete
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Perform search when debounced search term changes
  useEffect(() => {
    const performSearch = async () => {
      if (debouncedSearchTerm.trim().length < 2) {
        setSearchResults([]);
        setSearchError(null);
        return;
      }

      setSearching(true);
      setSearchError(null);

      try {
        const results = await searchStudents(debouncedSearchTerm);
        setSearchResults(results);

        if (results.length === 0) {
          setSearchError('No students found matching your search');
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to search students';
        setSearchError(errorMessage);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    performSearch();
  }, [debouncedSearchTerm]);

  const handleClose = () => {
    if (!linking) {
      onClose();
    }
  };

  const handleStudentSelect = (student: Student) => {
    // Prevent selecting already-linked students
    if (student.isLinked) {
      return;
    }
    setSelectedStudent(student);
    setError(null);
  };

  const handleLink = async () => {
    if (!selectedStudent) {
      setError('Please select a student to link');
      return;
    }

    setLinking(true);
    setError(null);

    try {
      await linkStudent(selectedStudent.email);
      handleClose();
      onSuccess();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to link student';
      setError(errorMessage);
    } finally {
      setLinking(false);
    }
  };

  const getDisplayName = (student: Student): string => {
    if (student.firstName && student.lastName) {
      return `${student.firstName} ${student.lastName}`;
    }
    return student.email;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableAutoFocus
      disableEnforceFocus
    >
      <DialogTitle>Link Student Account</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" paragraph>
            Search for a student by name or email address, then select them to link to your class.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Search Students"
          placeholder="Enter name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={linking}
          inputRef={searchInputRef}
          autoFocus
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searching && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            )
          }}
          helperText={
            searchTerm.length > 0 && searchTerm.length < 2
              ? 'Enter at least 2 characters to search'
              : ''
          }
        />

        {/* Search Results */}
        {searchTerm.trim().length >= 2 && (
          <Paper
            variant="outlined"
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              mb: 2
            }}
          >
            {searching && searchResults.length === 0 && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <CircularProgress size={30} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Searching...
                </Typography>
              </Box>
            )}

            {!searching && searchError && (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {searchError}
                </Typography>
              </Box>
            )}

            {!searching && searchResults.length > 0 && (
              <List disablePadding>
                {searchResults.map((student) => {
                  const isLinked = student.isLinked === true;
                  return (
                    <ListItem key={student.id} disablePadding>
                      <ListItemButton
                        selected={selectedStudent?.id === student.id}
                        onClick={() => handleStudentSelect(student)}
                        disabled={linking || isLinked}
                        sx={{
                          opacity: isLinked ? 0.6 : 1,
                          cursor: isLinked ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <PersonIcon sx={{ mr: 2, color: 'text.secondary' }} />
                          <ListItemText
                            primary={getDisplayName(student)}
                            secondary={
                              <>
                                {student.email}
                                {isLinked && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{ ml: 1, color: 'success.main', display: 'inline-flex', alignItems: 'center' }}
                                  >
                                    <CheckCircleIcon sx={{ fontSize: 14, mr: 0.5 }} />
                                    Already linked
                                  </Typography>
                                )}
                              </>
                            }
                            primaryTypographyProps={{
                              fontWeight: selectedStudent?.id === student.id ? 600 : 400
                            }}
                          />
                        </Box>
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </Paper>
        )}

        {/* Selected Student Display */}
        {selectedStudent && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: 'success.50',
              borderColor: 'success.main',
              mb: 2
            }}
          >
            <Typography variant="subtitle2" color="success.dark" gutterBottom>
              Selected Student:
            </Typography>
            <Typography variant="body1" fontWeight={600}>
              {getDisplayName(selectedStudent)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedStudent.email}
            </Typography>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={linking}>
          Cancel
        </Button>
        <Button
          onClick={handleLink}
          variant="contained"
          color="success"
          disabled={!selectedStudent || linking}
          startIcon={linking ? <CircularProgress size={20} /> : null}
        >
          {linking ? 'Linking...' : 'Link Student'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default LinkStudentDialog;
